import { ChevronDown, ChevronUp, Mic, AlertTriangle } from 'lucide-react';
import { useState } from 'react';

const DIFF_COLOR = {
  easy:   'border-l-green-400',
  medium: 'border-l-[#2563eb]',
  deep:   'border-l-purple-500',
};

const DIM_LABEL = {
  core_skills:  'Core Skills',
  domain_env:   'Domain',
  exp_depth:    'Exp Depth',
  seniority:    'Seniority',
  achievements: 'Achievements',
};

const scoreTextColor = (s) =>
  s >= 80 ? 'text-emerald-700' : s >= 60 ? 'text-[#2563eb]' : s >= 40 ? 'text-amber-700' : 'text-red-600';

/**
 * ScreeningSection — shows ai_screening_questions + voice_screening_score.
 */
export function ScreeningSection({ candidate }) {
  const questions = candidate.ai_screening_questions || [];
  const voice     = candidate.voice_screening_score;
  const [showAll, setShowAll] = useState(false);

  const visibleQs = showAll ? questions : questions.slice(0, 5);
  const hasContent = questions.length > 0 || voice;
  if (!hasContent) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-6"
         data-testid="screening-section">
      <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-2">
        <Mic className="w-5 h-5 text-[#2563eb]" />
        Screening
      </h2>

      {/* AI Screening Questions */}
      {questions.length > 0 && (
        <div className="mb-6" data-testid="ai-screening-questions">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            AI Screening Questions
            <span className="ml-2 text-[10px] font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
              {questions.length} questions
            </span>
          </h3>
          <div className="space-y-2">
            {visibleQs.map((q) => (
              <div key={q.id}
                   className={`bg-slate-50 rounded-lg p-3.5 border-l-4 ${DIFF_COLOR[q.difficulty] || 'border-l-slate-300'}`}
                   data-testid={`screening-q-${q.id}`}>
                <p className="text-sm text-slate-800 leading-relaxed mb-1.5">{q.text}</p>
                <div className="flex items-center gap-3 text-[10px] text-slate-400">
                  <span className="bg-white px-2 py-0.5 rounded border border-slate-200">{q.id}</span>
                  <span>{DIM_LABEL[q.dimension] || q.dimension}</span>
                  <span className="text-slate-600 font-medium">{q.target_skill}</span>
                  <span className={`font-semibold capitalize ${
                    q.difficulty === 'easy' ? 'text-green-600' :
                    q.difficulty === 'deep' ? 'text-purple-600' : 'text-[#2563eb]'
                  }`}>{q.difficulty}</span>
                </div>
              </div>
            ))}
          </div>
          {questions.length > 5 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="mt-3 flex items-center gap-1 text-xs text-slate-500 hover:text-[#2563eb] transition-colors"
              data-testid="show-all-questions-btn"
            >
              {showAll
                ? <><ChevronUp className="w-3.5 h-3.5" />Show less</>
                : <><ChevronDown className="w-3.5 h-3.5" />Show {questions.length - 5} more questions</>}
            </button>
          )}
        </div>
      )}

      {/* Voice Screening Score */}
      {voice ? (
        <div className={`border-t ${questions.length > 0 ? 'pt-5' : ''}`} data-testid="voice-screening-score">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-700">Voice Screening Score</h3>
            <div className="flex items-center gap-2">
              <span className={`text-3xl font-bold ${scoreTextColor(voice.overall_score ?? 0)}`}>
                {voice.overall_score ?? '—'}
              </span>
              <span className="text-slate-400 text-sm">/100</span>
            </div>
          </div>
          {voice.recommendation && (
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-blue-50 text-[#2563eb] border border-blue-100 mb-3">
              {voice.recommendation}
            </span>
          )}
          {voice.red_flags_global?.length > 0 && (
            <div className="mt-3 space-y-1">
              {voice.red_flags_global.map((f) => (
                <div key={f} className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{f}
                </div>
              ))}
            </div>
          )}
          {voice.scored_at && (
            <p className="text-[10px] text-slate-400 mt-3">
              Scored {new Date(voice.scored_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
      ) : questions.length > 0 ? (
        <div className="border-t pt-5">
          <div className="text-center py-4 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
            <p className="text-sm text-slate-400">No voice screening completed yet</p>
            <p className="text-xs text-slate-400 mt-0.5">Schedule a Zoom call to score live responses</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
